import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { MedataContext } from '../../../../../../ContexMedata';

// Formatos que el servidor entrega recortados: no son el dataset completo, así
// que conviene decirlo antes de la descarga y no dentro del archivo.
const TRUNCATED_FORMATS = {
  pdf: 'El PDF incluye solo las primeras 500 filas (es una vista previa).',
  xlsx: 'El Excel incluye hasta 50.000 filas.'
};

function FileDownloadMultiFormat({ title, distributions, id, description }) {
  const { datasetDonwloads, setDatasetDonwloads } = useContext(MedataContext);
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [isPreparing, setIsPreparing] = useState(false);
  const [conversionError, setConversionError] = useState(null);
  // Tamaño del CSV de origen, para avisar de descargas lentas antes de pulsar.
  const [sourceInfo, setSourceInfo] = useState(null);

  // Obtener la URL del CSV original
  const csvDistribution = distributions && distributions.length > 0 ? distributions[0] : null;
  const currentUrl = window.location.href;

  // Formatos disponibles para descargar
  const availableFormats = [
    { value: 'csv', label: 'CSV', icon: 'fa-file-csv' },
    { value: 'xlsx', label: 'Excel (XLSX)', icon: 'fa-file-excel' },
    { value: 'json', label: 'JSON', icon: 'fa-file-code' },
    { value: 'xml', label: 'XML', icon: 'fa-file-code' },
    { value: 'pdf', label: 'PDF', icon: 'fa-file-pdf' }
  ];

  const getCsvUrl = () => {
    if (!csvDistribution) return '';
    const downloadURL = csvDistribution.data.downloadURL || csvDistribution.data.accessURL || '';

    // Reemplazar data-nginx por la ruta del proxy para local
    let finalUrl = downloadURL;

    // Si la URL es de data-nginx (red interna), usar el proxy
    if (finalUrl.includes('data-nginx')) {
      finalUrl = finalUrl.replace('http://data-nginx/data/', '/data-files/');
    }

    // Si la página está en HTTPS, asegurar que la URL también lo esté
    if (currentUrl.startsWith('https://') && finalUrl.startsWith('http://')) {
      finalUrl = finalUrl.replace('http://', 'https://');
    }

    return finalUrl;
  };

  // Obtener la URL absoluta del CSV para el backend
  const getAbsoluteCsvUrl = () => {
    if (!csvDistribution) return '';
    let csvUrl = csvDistribution.data.downloadURL || csvDistribution.data.accessURL || '';

    // Si la URL es relativa, convertirla a absoluta usando el dominio actual
    if (csvUrl.startsWith('/')) {
      const origin = window.location.origin; // http://localhost o https://medata.gov.co
      csvUrl = origin + csvUrl;
    }

    return csvUrl;
  };

  // URL absoluta del CSV, memorizada como string: se usa como dependencia del
  // efecto de abajo. Si dependiéramos del objeto `csvDistribution`, cada render
  // del padre le cambiaría la identidad y relanzaría la petición en bucle.
  const absoluteCsvUrl = getAbsoluteCsvUrl();

  // Consultar el tamaño del CSV una sola vez, para poder advertir de antemano.
  useEffect(() => {
    let cancelled = false;
    if (!absoluteCsvUrl) return undefined;

    axios
      .get(`/api/dataset/${id}/info-descarga`, { params: { u: absoluteCsvUrl } })
      .then(({ data }) => {
        if (!cancelled) setSourceInfo(data);
      })
      .catch(() => {
        // El aviso es informativo: si falla, la descarga sigue disponible.
        if (!cancelled) setSourceInfo(null);
      });

    return () => {
      cancelled = true;
    };
  }, [id, absoluteCsvUrl]);

  // Registra la descarga en el backend: incrementa el contador y guarda la
  // referencia geográfica (país) de quien descarga. El país se determina en el
  // navegador por su ZONA HORARIA (sin usar la IP, por privacidad) y el backend
  // la resuelve a código de país ISO. Reemplaza el antiguo PATCH directo a
  // JSON:API que exponía credenciales de escritura en el bundle JS.
  const updateDownloadCounter = async () => {
    let timezone = '';
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      timezone = '';
    }

    try {
      const { data } = await axios.post(`/api/dataset/${id}/descarga`, { timezone });
      if (data && typeof data.descargas !== 'undefined') {
        setDatasetDonwloads(data.descargas);
      }
    } catch (error) {
      // No bloquear la descarga si el registro falla.
      console.error('Error al registrar la descarga:', error);
    }
  };

  const handleDownload = () => {
    setConversionError(null);

    // Si es CSV, descargar directamente
    if (selectedFormat === 'csv') {
      updateDownloadCounter();
      window.location.href = getCsvUrl();
      return;
    }

    // Para el resto de formatos, el servidor convierte EN STREAMING y va
    // enviando el archivo mientras lo genera. Al ser una URL normal, el navegador
    // la trata como cualquier descarga y muestra su propia barra de progreso; y
    // como salen bytes desde el principio, ningún proxy corta la conexión (era el
    // origen del error 504 al convertir archivos grandes).
    const streamUrl =
      `/api/dataset/${id}/descargar/${selectedFormat}` +
      `?u=${encodeURIComponent(absoluteCsvUrl)}` +
      `&title=${encodeURIComponent(title || 'Dataset')}`;

    updateDownloadCounter();

    // El navegador tarda un momento en recibir la primera parte y mostrar su
    // indicador; mientras tanto enseñamos el nuestro para que no parezca colgado.
    setIsPreparing(true);
    window.location.href = streamUrl;
    window.setTimeout(() => setIsPreparing(false), 6000);
  };

  if (!csvDistribution) {
    return null;
  }

  const handleFormatChange = (e) => {
    setSelectedFormat(e.target.value);
    setConversionError(null);
  };

  const selectedFormatData = availableFormats.find(f => f.value === selectedFormat);
  const isCsv = selectedFormat === 'csv';
  const showSlowWarning = !isCsv && sourceInfo && sourceInfo.slow;
  const truncationNote = !isCsv ? TRUNCATED_FORMATS[selectedFormat] : null;

  return (
    <div className='data-actions-multi'>
      <div className='format-selector-container'>
        <div className='format-selector'>
          <label htmlFor={`format-select-${id}`} className='format-label'>
            <i className='fa-solid fa-file-alt'></i>
            {' Formato de descarga:'}
          </label>
          <select
            id={`format-select-${id}`}
            className='format-select'
            value={selectedFormat}
            onChange={handleFormatChange}
            disabled={isPreparing}
          >
            {availableFormats.map((format) => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
        </div>

        <div className='data-actions pl-2'>
          <button
            type='button'
            title={`Descargar como ${selectedFormatData?.label}`}
            className='button button-secondary'
            onClick={handleDownload}
            disabled={isPreparing}
          >
            {isPreparing ? (
              <>
                <i className='fa-solid fa-spinner fa-spin'></i>
                {' Preparando descarga...'}
              </>
            ) : (
              <>
                <i className={`fa-solid ${selectedFormatData?.icon || 'fa-download'}`}></i>
                {` Descargar ${selectedFormatData?.label}`}
              </>
            )}
          </button>
        </div>
      </div>

      {isPreparing && (
        <div className='conversion-progress' role='status' aria-live='polite'>
          <i className='fa-solid fa-spinner fa-spin'></i>
          {' Generando el archivo. La descarga aparecerá en tu navegador en unos segundos; '}
          {'puedes seguir navegando mientras tanto.'}
        </div>
      )}

      {showSlowWarning && (
        <div className='conversion-warning'>
          <i className='fa-solid fa-triangle-exclamation'></i>
          {` Este conjunto de datos pesa ${sourceInfo.mb} MB. Convertirlo a `}
          {`${selectedFormatData?.label} puede tardar varios minutos. Si solo necesitas los `}
          {'datos, la descarga en CSV es inmediata.'}
        </div>
      )}

      {truncationNote && (
        <div className='conversion-note'>
          <i className='fa-solid fa-circle-info'></i>
          {' ' + truncationNote}
        </div>
      )}

      {conversionError && (
        <div className='conversion-error'>
          <i className='fa-solid fa-exclamation-triangle'></i>
          {' ' + conversionError}
        </div>
      )}
    </div>
  );
}

export default FileDownloadMultiFormat;
